import { Box, Text, Flex, ColorsV2, spaces } from '@electricjs/arc';

import { useIsEmbedded } from '@/hooks/useIsEmbedded';

type EmbedBlockerProps = {
  children: React.ReactNode;
};

/**
 * Component that provides bidirectional iframe protection:
 *
 * 1. Blocks regular routes (outside /embed/*) from being accessed in iframes
 * 2. Blocks /embed/* routes from being accessed outside of iframes
 * 3. Blocks /embed/* routes from unauthorized iframe origins
 *
 * This ensures:
 * - Only /embed/* routes can be embedded
 * - Regular routes cannot be embedded
 * - All embedding must be from authorized origins
 *
 * Note: Feature flag logic is handled at the route level in routes.tsx.
 */
export const EmbedBlocker = ({ children }: EmbedBlockerProps) => {
  const { shouldBlock, parentOrigin, isEmbedded, isEmbeddedRoute } =
    useIsEmbedded();

  if (shouldBlock) {
    // Case 1: Regular route accessed in an iframe - BLOCK IT
    if (isEmbedded && !isEmbeddedRoute) {
      return (
        <Flex
          justifyContent="center"
          alignItems="center"
          height="100vh"
          backgroundColor={ColorsV2.BACKGROUND}>
          <Box textAlign="center" p={spaces.space_l}>
            <Text variant="heading-2" mb={spaces.space_s_rem}>
              Embedding Not Allowed
            </Text>
            <Text intent="secondary">
              This page cannot be displayed in an iframe.
            </Text>
            <Text intent="secondary" mt={spaces.space_m_rem}>
              Please access this page directly in your browser.
            </Text>
            {parentOrigin && (
              <Text variant="body-2" intent="secondary" mt={spaces.space_m_rem}>
                Parent Origin: {parentOrigin}
              </Text>
            )}
          </Box>
        </Flex>
      );
    }

    // Case 2: Trying to access /embed/* routes in the main app (not in iframe)
    if (!isEmbedded && isEmbeddedRoute) {
      return (
        <Flex
          justifyContent="center"
          alignItems="center"
          height="100vh"
          backgroundColor={ColorsV2.BACKGROUND}>
          <Box textAlign="center" p={spaces.space_l}>
            <Text variant="heading-2" mb={spaces.space_m_rem}>
              Embedded-Only Route
            </Text>
            <Text intent="secondary">
              This page is only accessible when embedded in an authorized
              iframe.
            </Text>
            <Text variant="body-2" intent="secondary" mt={spaces.space_m_rem}>
              Please access this page through the main application.
            </Text>
          </Box>
        </Flex>
      );
    }

    // Case 3: Embedded in unauthorized context
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        height="100vh"
        backgroundColor={ColorsV2.BACKGROUND}>
        <Box textAlign="center" p={spaces.space_l}>
          <Text variant="heading-2" mb={spaces.space_m_rem}>
            Unauthorized Embedding
          </Text>
          <Text intent="secondary">
            This page cannot be displayed in an iframe from this domain.
          </Text>
          {parentOrigin && (
            <Text variant="body-2" intent="secondary" mt={spaces.space_m_rem}>
              Origin: {parentOrigin}
            </Text>
          )}
        </Box>
      </Flex>
    );
  }

  return <>{children}</>;
};
